/* eslint-disable no-console */
import Helix from '@charter/helix/build/portals/index.min.js';
import pkgJson from '../../../package.json';
import { IHelixAnalyticsConfig } from '../../models/HelixConfig';
import { CURRENT_SITE } from '../../../config-sites';

const isPolicy = CURRENT_SITE?.id === `ga` ?? false;
const isBrowser = typeof window !== `undefined`;

export const HELIX_APP_CONFIG: IHelixAnalyticsConfig = {
    domain: `portals`,
    appName: isPolicy ? `CharterPublicPolicy` : `CEW`,
    appType: `Web`,
    startSession: isPolicy
        ? `CharterPublicPolicy_startSession_generic`
        : `CEW_startSession`,
    requirementsVersion: isPolicy ? `2.552` : `2.560`,
    appVersion: pkgJson.version,
    debugAppEvents: false,
};
export default class HelixAnalyticsService {
    config: IHelixAnalyticsConfig;
    constructor(config: IHelixAnalyticsConfig) {
        try {
            if (config) {
                this.config = config;
            } else {
                console.error(
                    `Analytics Service | Error - No Config or Version provided.`
                );
                return;
            }
        } catch (e) {
            console.error(`Analytics Service | Error on Constructor : ${e}`);
            return;
        }
        this.init = this.init.bind(this);
        this.track = this.track.bind(this);
        this.trackPageView = this.trackPageView.bind(this);
        this.init();
    }

    init = () => {
        let endpoint;
        let env;
        if (!isBrowser) {
            return null;
        }

        try {
            Helix.useDefaultStorage();
            if (
                window.location.host === `corporate.charter.com` ||
                window.location.host === `policy.charter.com` ||
                window.location.host.includes(`charter.com`)
            ) {
                env = `prod`;
                endpoint = `https://collector.pi.spectrum.net/topics/sspp?bulk=true`;
            } else {
                if (window.location.host.includes(`localhost`)) {
                    this.config.debugAppEvents = true;
                    env = `local`;
                } else {
                    env = `staging`;
                }
                endpoint = `https://collector.pi-charter.net/topics/sspp?bulk=true`;
            }

            this.config.appEnvName = env;

            Helix.set(`endpoint`, endpoint);

            Helix.init(this.config)
                .then(() => {
                    if (Helix.get(`currentState`) === `blank`) {
                        Helix.trackStartSession(this.config.startSession);
                    }
                })
                .catch((e) => console.error(e, `INIT ERROR`));
        } catch (err) {
            console.error(err);
        }
    };

    /**
     * @doc method
     * @name analytics#track
     * @methodOf analytics
     * @returns {}
     * @description Calls Helix Track
     */
    track(id, adHocData?, options?) {
        if (!isBrowser) {
            return null;
        }
        try {
            Helix.track(id, adHocData, options);
        } catch (err) {
            if (this.config.debugAppEvents) {
                console.error(err);
            }
        }
    }

    /**
     * @doc method
     * @name analytics#trackPageView
     * @methodOf analytics
     * @returns {}
     * @description Calls Helix Track with a Complete PageView Cycle (Init-Completed). To be used when no Page load time calculation is required.
     */
    trackPageView(pageName, pageType?, adHocData?) {
        if (!isBrowser) {
            return null;
        }
        const appSection = window.location.pathname.split(`/`)?.[1] ?? `root`;
        try {
            Helix.track(`pageViewInit`, {
                currPageName: pageName ?? window.location.pathname,
                currPagePageType: pageType ?? ``,
                currPageAppSection: appSection,
                msgTriggeredBy: `user`,
                ...adHocData,
            });
            Helix.track(`pageView`, {}, { eventType: `pageViewCompleted` });
        } catch (err) {
            if (this.config.debugAppEvents) {
                console.error(err);
            }
        }
    }

    set(id, value) {
        try {
            Helix.set(id, value);
        } catch (err) {
            if (this.config.debugAppEvents) {
                console.error(err);
            }
        }
    }
    get(value) {
        try {
            return Helix.get(value);
        } catch (err) {
            if (this.config.debugAppEvents) {
                console.error(err);
            }
        }
    }
}

export const helixAnalytics = new HelixAnalyticsService(HELIX_APP_CONFIG);
